<script setup>
/**
 * FormControlMRegistrantsRegistrantsLogin
 * Secure automated customer session portal simulator button.
 * Pulls relational user access parameters out of remote category bridges,
 * contextually exposing target sign-in routes for entities assigned to credentialed active groups.
 */
import { ref, computed, watch, onMounted, getCurrentInstance } from 'vue';
import Button from "../Button.vue";

const props = defineProps({
    // Standard Orchestrator Props
    formId: {
        type: String,
        required: true
    },
    field: {
        type: Object,
        required: true
    },
    currentData: {
        type: Object,
        required: true
    }
});

const { proxy } = getCurrentInstance();
const store = proxy.$store;

// --- State Layers ---

const categoryIds = ref([]);
const hasAccessGroups = ref(false);
const isLoading = ref(false);

// --- Synchronous Computeds ---

/**
 * Normalizes email lookup addresses while safely preserving historical fallback configurations.
 */
const email = computed(() => {
    // username is deprecated
    return props.currentData?.username || props.currentData?.email || '';
});

/**
 * Validates baseline criteria configurations to show or hide the login button.
 */
const showButton = computed(() => {
    return hasAccessGroups.value && email.value.trim().length > 0;
});

const registrantId = computed(() => props.currentData?.id);

// --- Methods ---

/**
 * Synchronous Visibility Adjuster
 * Updates the fields configuration registry properties directly based on state validity flags.
 */
const updateLayoutVisibility = () => {
    if (props.field) {
        props.field.visibility = showButton.value ? '' : 'hidden';
    }
};

/**
 * Sequential Access Evaluator
 * Runs a unified data retrieval sequence to prevent layout race conditions.
 */
const evaluateAccessControl = async () => {
    if (!registrantId.value) {
        categoryIds.value = [];
        hasAccessGroups.value = false;
        updateLayoutVisibility();
        return;
    }

    isLoading.value = true;
    try {
        // Step 1: Collect underlying relational layout linkages
        const columnData = await store.dispatch('categories/getTableColumnData', {
            tableName: 'm_registrants_registrants',
            columnName: 'contactGroup'
        });

        const activeColumnArray = columnData || [];
        const filteredCatIds = activeColumnArray
            .filter(o => o.itemId === registrantId.value)
            .map(o => o.catId);

        categoryIds.value = filteredCatIds;

        // Step 2: Immediately calculate group permissions using the resolved array
        if (filteredCatIds.length > 0) {
            const preloadedGroups = await store.dispatch('itemData/preload', {
                tableName: 'm_registrants_groups',
                ids: filteredCatIds,
            });

            const matchingGroupArray = preloadedGroups || [];
            hasAccessGroups.value = matchingGroupArray.filter(o => !!o.accessGroup).length > 0;
        } else {
            hasAccessGroups.value = false;
        }
    } catch (err) {
        console.error('Operational crash validating registrant permission boundaries:', err);
        categoryIds.value = [];
        hasAccessGroups.value = false;
    } finally {
        isLoading.value = false;
        updateLayoutVisibility();
    }
};

/**
 * Requests a temporary cryptographically signed auto-login vector string
 * and securely transfers the administrative session out to the public front-end endpoint.
 */
const handleAutoLogin = async () => {
    if (!registrantId.value) return;

    try {
        const response = await store.dispatch('request/get', {
            url: 'api/customer/get-auto-login-key',
            params: { customerId: registrantId.value }
        });

        const loginKey = response?.data?.key;
        const targetHostEndpoint = store.state.websiteEndpoint;

        if (loginKey && targetHostEndpoint) {
            window.open(`${targetHostEndpoint}/controller/Customers/autoLogin/${loginKey}`);
        }
    } catch (err) {
        console.error('Automated authentication processing routing failure:', err);
    }
};

// --- Watchers & Initializer Lifecycle ---

// Re-evaluate verification states cleanly whenever the primary tracking index changes
watch(registrantId, async () => {
    await evaluateAccessControl();
});

// Update visibility properties cleanly whenever button conditions toggle
watch(showButton, () => {
    updateLayoutVisibility();
});

onMounted(async () => {
    // Initial evaluations run sequentially on mount
    await evaluateAccessControl();
});
</script>

<template>
    <div class="form-control-registrants-login-wrapper">

        <Button
            v-if="showButton && !isLoading"
            class="btn-light border btn-sm"
            @click.native="handleAutoLogin"
        >
            Login
        </Button>

    </div>
</template>

<style scoped>
/* Interface parameters route through global corporate layout designs natively */
</style>